import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

/**
 * KpSelect — Custom dropdown following KanPlan designsystem Del P.
 *
 * Props:
 *   value        — currently selected value (string)
 *   onChange      — (newValue: string) => void
 *   options       — [{ value, label, dot?, disabled? }]
 *   placeholder   — placeholder text when no value selected
 *   disabled      — disable the trigger
 *   icon          — optional leading React node / icon
 *   style         — extra style on the outer wrapper
 *   triggerStyle  — extra style on the trigger button
 *   small         — if true, uses compact sizing (height 34, fontSize 12)
 *   required      — mirrors <select required> for a11y
 *   name          — field name (for form context)
 *   portalTarget  — DOM element to portal into (default: document.body)
 *   searchable    — enable search input inside dropdown (for long lists)
 *   maxHeight     — max dropdown height in px (default 240)
 */

const EASE = '0.15s cubic-bezier(.4,0,.2,1)';

/* Placering (Del P6, beslut 2026-10-20): listan öppnas ALLTID under triggern
   med 4 px glapp, aldrig uppåt. Räcker inte utrymmet krymper listan med
   intern scroll, dock aldrig under MIN_LIST_H — då rullas de scrollbara
   föräldrarna (sida, modal, KpPanel) fram innan listan öppnas. Kan inget
   rullas längre (t.ex. nedersta fältet i en panel) krymper listan hellre
   inom fönstret, ner till två rader (LAST_RESORT_H), än att hamna utanför det. */
const GAP = 4;
const EDGE = 8;
const MIN_LIST_H = 160;
const LAST_RESORT_H = 72; // två rader

function missingBelow(el) {
  return el.getBoundingClientRect().bottom + GAP + MIN_LIST_H + EDGE - window.innerHeight;
}

/* Rullar fram triggern så att minst MIN_LIST_H får plats under den:
   närmaste scrollbara förälder först, sedan nästa utåt, sist fönstret. */
function makeRoomBelow(el) {
  if (!el || missingBelow(el) <= 0) return;
  let node = el.parentElement;
  while (node && node !== document.body) {
    const { overflowY, position } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && typeof node.scrollBy === 'function') {
      const room = node.scrollHeight - node.clientHeight - node.scrollTop;
      if (room > 0) node.scrollBy({ top: Math.min(room, missingBelow(el)) });
      if (missingBelow(el) <= 0) return;
    }
    // Fast panel/modal: att rulla sidan bakom flyttar inte triggern.
    if (position === 'fixed') return;
    node = node.parentElement;
  }
  if (typeof window.scrollBy === 'function') window.scrollBy({ top: missingBelow(el) });
}

/* Tokens som CSS-variabler ur index.css — så att komponenten följer mörkt läge.
   (Tidigare låg ljusa hex-värden hårdkodade här, och dropdownen blev vit i
   mörkt läge i alla moduler som använder den.) */
const TOKENS = {
  inset: 'var(--kp-inset)',
  white: 'var(--kp-white)',
  text: 'var(--kp-text)',
  textFaint: 'var(--kp-text-faint)',
  border: 'var(--kp-border)',
  accent: 'var(--kp-accent)',
  accentHover: 'var(--kp-accent-hover)',
  accentLight: 'var(--kp-accent-light)',
  accentRing: 'var(--kp-accent-ring-modal)',
  shadow: 'var(--shadow-lg)',
  radiusMd: 'var(--radius-md)',
};

export default function KpSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Välj…',
  disabled = false,
  icon = null,
  style,
  triggerStyle,
  small = false,
  required = false,
  name,
  portalTarget,
  searchable = false,
  maxHeight = 240,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, height: 0 });

  const selectedOption = useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter((o) => (o.label || String(o.value)).toLowerCase().includes(q));
  }, [options, search, searchable]);

  /* Position calculation — dropdown appears UNDER trigger via portal */
  const computePos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const fullH = maxHeight + (searchable ? 44 : 0) + 8;
    const spaceBelow = window.innerHeight - rect.bottom - GAP - EDGE;
    const floor = Math.min(MIN_LIST_H, fullH);
    setPos({
      top: rect.bottom + GAP,
      left: rect.left,
      width: Math.max(rect.width, 160),
      height: spaceBelow >= floor
        ? Math.min(fullH, spaceBelow)
        : Math.max(spaceBelow, Math.min(LAST_RESORT_H, fullH)),
    });
  }, [maxHeight, searchable]);

  /* Open/close handlers */
  const toggle = useCallback(() => {
    if (disabled) return;
    if (!open) makeRoomBelow(triggerRef.current);
    setOpen((v) => {
      if (!v) {
        setSearch('');
        requestAnimationFrame(computePos);
      }
      return !v;
    });
  }, [disabled, open, computePos]);

  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  /* Close on outside click */
  useEffect(() => {
    if (!open) return;
    computePos();
    const handle = (e) => {
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        close();
      }
    };
    const onReflow = () => computePos();
    document.addEventListener('mousedown', handle);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('mousedown', handle);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, close, computePos]);

  /* Focus search input when dropdown opens */
  useEffect(() => {
    if (open && searchable) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [open, searchable]);

  /* Escape medan listan är öppen stänger BARA listan — inte dialogen den ligger
     i. Lyssnar på window i capture-fasen, som körs före både document-lyssnare
     och React-hanterare, så att ingen modal hinner tolka samma tangent som
     "stäng dialogen" (A18, produktionsgenomgången 2026-10-01). */
  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      close();
      triggerRef.current?.focus();
    };
    window.addEventListener('keydown', onEsc, true);
    return () => window.removeEventListener('keydown', onEsc, true);
  }, [open, close]);

  /* Keyboard: Escape closes */
  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    },
    [close],
  );

  const handleSelect = useCallback(
    (optValue) => {
      onChange?.(optValue);
      close();
      triggerRef.current?.focus();
    },
    [onChange, close],
  );

  /* Sizes */
  const h = small ? 34 : 42;
  const fs = small ? 12 : 13.5;
  const fw = small ? 600 : 500;

  const dropdown = open
    ? createPortal(
        <div
          ref={dropdownRef}
          role="listbox"
          onKeyDown={onKeyDown}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: pos.width,
            maxHeight: pos.height || maxHeight + (searchable ? 44 : 0) + 8,
            overflowY: 'auto',
            overflowX: 'hidden',
            background: TOKENS.white,
            border: `1px solid ${TOKENS.border}`,
            borderRadius: TOKENS.radiusMd,
            boxShadow: TOKENS.shadow,
            padding: 4,
            // 700 överstiger modal-overlay (600) så dropdownen syns även inuti modaler.
            zIndex: 700,
            animation: 'kpSelectFadeUp 0.15s ease-out',
          }}
        >
          {searchable && (
            <div style={{ padding: '6px 8px', marginBottom: 4, borderBottom: `1px solid ${TOKENS.border}` }}>
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Sök…"
                onKeyDown={onKeyDown}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  border: `1px solid ${TOKENS.border}`,
                  borderRadius: 6,
                  background: TOKENS.inset,
                  color: TOKENS.text,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = TOKENS.accent;
                  e.currentTarget.style.background = TOKENS.white;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = TOKENS.border;
                  e.currentTarget.style.background = TOKENS.inset;
                }}
              />
            </div>
          )}
          {filteredOptions.length === 0 && (
            <div style={{ padding: '9px 12px', fontSize: 13, color: TOKENS.textFaint }}>Inga alternativ</div>
          )}
          {filteredOptions.map((opt) => {
            const isSelected = String(opt.value) === String(value);
            return (
              <div
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                onClick={() => !opt.disabled && handleSelect(String(opt.value))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '9px 12px',
                  fontSize: 13,
                  fontWeight: isSelected ? 600 : 500,
                  color: opt.disabled ? TOKENS.textFaint : isSelected ? TOKENS.accent : TOKENS.text,
                  borderRadius: 6,
                  cursor: opt.disabled ? 'default' : 'pointer',
                  transition: `background ${EASE}`,
                  opacity: opt.disabled ? 0.5 : 1,
                  background: 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!opt.disabled) e.currentTarget.style.background = TOKENS.accentLight;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {/* Checkmark space */}
                <span style={{ width: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isSelected && (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={TOKENS.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3.5 8.5l3 3 6-6.5" />
                    </svg>
                  )}
                </span>
                {/* Optional status dot */}
                {opt.dot && (
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: opt.dot,
                      flexShrink: 0,
                    }}
                  />
                )}
                {/* Långa ord radbryts inom listan, annars får den rullning i sidled. */}
                <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{opt.label || String(opt.value)}</span>
              </div>
            );
          })}
        </div>,
        portalTarget || document.body,
      )
    : null;

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative', ...style }}
      onKeyDown={onKeyDown}
    >
      {/* Hidden input for form submit / required validation */}
      {name && <input type="hidden" name={name} value={value ?? ''} required={required} />}

      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={toggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          padding: '0 12px',
          height: h,
          background: open ? TOKENS.white : TOKENS.inset,
          border: `1.5px solid ${open ? TOKENS.accentHover : 'transparent'}`,
          borderRadius: TOKENS.radiusMd,
          fontFamily: 'inherit',
          fontSize: fs,
          fontWeight: fw,
          color: TOKENS.text,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: `all ${EASE}`,
          textAlign: 'left',
          outline: 'none',
          boxShadow: open ? `0 0 0 3px ${TOKENS.accentRing}` : 'none',
          opacity: disabled ? 0.5 : 1,
          boxSizing: 'border-box',
          ...triggerStyle,
        }}
        onMouseEnter={(e) => {
          if (!open && !disabled) e.currentTarget.style.borderColor = TOKENS.border;
        }}
        onMouseLeave={(e) => {
          if (!open && !disabled) e.currentTarget.style.borderColor = 'transparent';
        }}
        onFocus={(e) => {
          if (!open) {
            e.currentTarget.style.borderColor = TOKENS.accentHover;
            e.currentTarget.style.background = TOKENS.white;
            e.currentTarget.style.boxShadow = `0 0 0 3px ${TOKENS.accentRing}`;
          }
        }}
        onBlur={(e) => {
          if (!open) {
            e.currentTarget.style.borderColor = 'transparent';
            e.currentTarget.style.background = TOKENS.inset;
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      >
        {icon && <span style={{ display: 'flex', flexShrink: 0 }}>{icon}</span>}
        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: selectedOption ? TOKENS.text : TOKENS.textFaint,
            fontWeight: selectedOption ? fw : 400,
          }}
        >
          {selectedOption ? (selectedOption.label || String(selectedOption.value)) : placeholder}
        </span>
        <ChevronDown
          size={small ? 14 : 15}
          style={{
            marginLeft: 'auto',
            flexShrink: 0,
            color: TOKENS.textFaint,
            transition: `transform ${EASE}`,
            transform: open ? 'rotate(180deg)' : 'none',
          }}
          strokeWidth={2.5}
        />
      </button>
      {dropdown}
    </div>
  );
}
